import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Crown,
  Users,
  DollarSign,
  TrendingUp,
  MessageSquare,
  Search,
  Lock,
  Unlock,
  Send,
  Eye,
  LogOut,
  Settings,
  Bell,
  Mail,
  Star,
  Megaphone,
  Plus,
  Trash2,
  Calendar,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Tab State: "stats" | "users" | "messages" | "analytics" | "cashflow"
  const [activeTab, setActiveTab] = useState("stats");

  // Cashflow Management States
  const [cashflowData, setCashflowData] = useState(null);
  const [cashflowLoading, setCashflowLoading] = useState(false);
  const [txTypeFilter, setTxTypeFilter] = useState("all");
  const [txCatFilter, setTxCatFilter] = useState("all");

  // Form State for Recording Transaction
  const [showTxModal, setShowTxModal] = useState(false);
  const [txType, setTxType] = useState("expense"); // expense or income
  const [txCategory, setTxCategory] = useState("server_cost");
  const [txAmount, setTxAmount] = useState("");
  const [txDescription, setTxDescription] = useState("");
  const [recordingTx, setRecordingTx] = useState(false);

  // Admin Data States
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [trafficData, setTrafficData] = useState(null);
  const [trafficTimeframe, setTrafficTimeframe] = useState("day"); // "day" | "hour"

  // Messaging Form State
  const [msgTitle, setMsgTitle] = useState("");
  const [msgContent, setMsgContent] = useState("");
  const [msgTarget, setMsgTarget] = useState("broadcast"); // "broadcast" | parentId
  const [sendingMsg, setSendingMsg] = useState(false);

  // Ads Management States
  const [ads, setAds] = useState([]);
  const [showAdModal, setShowAdModal] = useState(false);
  const [isEditingAd, setIsEditingAd] = useState(false);
  const [editingAdId, setEditingAdId] = useState(null);
  
  // Ad Form Fields
  const [advertiserName, setAdvertiserName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [placement, setPlacement] = useState("parent_corner_header");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isActive, setIsActive] = useState(true);

  const resetAdForm = () => {
    setAdvertiserName("");
    setImageUrl("");
    setTargetUrl("");
    setPlacement("parent_corner_header");
    const nowStr = new Date().toISOString().substring(0, 16);
    const endStr = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().substring(0, 16);
    setStartDate(nowStr);
    setEndDate(endStr);
    setIsActive(true);
  };

  // Verify login token on mount
  useEffect(() => {
    const token = sessionStorage.getItem("admin_token");
    if (token) {
      verifyAdminTokenOnBackend(token);
    }
  }, []);

  const verifyAdminTokenOnBackend = async (token) => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${baseUrl}/api/admin/verify-token`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        setIsLoggedIn(true);
        fetchAdminData(token);
      } else {
        handleLogout();
      }
    } catch (err) {
      handleLogout();
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${baseUrl}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        sessionStorage.setItem("admin_token", data.token);
        setIsLoggedIn(true);
        toast.success("Login Admin Berhasil 👑");
        fetchAdminData(data.token);
      } else {
        toast.error(data.detail || "Username atau password salah");
      }
    } catch (err) {
      toast.error("Gagal terhubung ke server backend");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_token");
    setIsLoggedIn(false);
    toast.info("Logout berhasil");
  };

  const fetchAdminData = async (token) => {
    const activeToken = token || sessionStorage.getItem("admin_token");
    if (!activeToken) return;
    const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";
    setCashflowLoading(true);
    try {
      const [statsRes, usersRes, adsRes, trafficRes, cashflowRes] = await Promise.all([
        fetch(`${baseUrl}/api/admin/stats`, {
          headers: { "Authorization": `Bearer ${activeToken}` }
        }),
        fetch(`${baseUrl}/api/admin/users`, {
          headers: { "Authorization": `Bearer ${activeToken}` }
        }),
        fetch(`${baseUrl}/api/admin/ads`, {
          headers: { "Authorization": `Bearer ${activeToken}` }
        }),
        fetch(`${baseUrl}/api/admin/traffic-analytics`, {
          headers: { "Authorization": `Bearer ${activeToken}` }
        }),
        fetch(`${baseUrl}/api/financial/cashflow`, {
          headers: { "Authorization": `Bearer ${activeToken}` }
        }),
      ]);
      if (statsRes.status === 401 || usersRes.status === 401 || adsRes.status === 401 || trafficRes.status === 401 || cashflowRes.status === 401) {
        handleLogout();
        return;
      }
      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (adsRes.ok) setAds(await adsRes.json());
      if (trafficRes.ok) setTrafficData(await trafficRes.json());
      if (cashflowRes.ok) setCashflowData(await cashflowRes.json());
    } catch (err) {
      console.error("Gagal menyinkronkan data admin:", err);
    } finally {
      setCashflowLoading(false);
    }
  };

  const handleToggleFullAccess = async (user) => {
    const token = sessionStorage.getItem("admin_token");
    const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";
    try {
      const newStatus = !user.has_full_access;
      const res = await fetch(`${baseUrl}/api/admin/users/${user.id}`, {
        method: "PATCH",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ has_full_access: newStatus }),
      });
      if (res.status === 401) {
        handleLogout();
        return;
      }
      if (res.ok) {
        toast.success(`Berhasil memperbarui status ${user.name}`);
        fetchAdminData(token);
      } else {
        toast.error("Gagal mengubah status langganan");
      }
    } catch (err) {
      toast.error("Kesalahan jaringan");
    }
  };

  const handleToggleLayer = async (user, layerNum) => {
    const token = sessionStorage.getItem("admin_token");
    const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";
    try {
      let currentLayers = [...user.unlocked_layers];
      if (currentLayers.includes(layerNum)) {
        currentLayers = currentLayers.filter((l) => l !== layerNum);
      } else {
        currentLayers.push(layerNum);
      }
      const res = await fetch(`${baseUrl}/api/admin/users/${user.id}`, {
        method: "PATCH",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ unlocked_layers: currentLayers }),
      });
      if (res.status === 401) {
        handleLogout();
        return;
      }
      if (res.ok) {
        toast.success(`Berhasil mengubah layer untuk ${user.name}`);
        fetchAdminData(token);
      } else {
        toast.error("Gagal mengubah layer");
      }
    } catch (err) {
      toast.error("Kesalahan jaringan");
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!msgTitle || !msgContent) {
      toast.warning("Judul dan isi pesan tidak boleh kosong");
      return;
    }
    const token = sessionStorage.getItem("admin_token");
    setSendingMsg(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${baseUrl}/api/admin/messages`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          parent_id: msgTarget === "broadcast" ? null : msgTarget,
          title: msgTitle,
          content: msgContent,
        }),
      });
      if (res.status === 401) {
        handleLogout();
        return;
      }
      if (res.ok) {
        toast.success(
          msgTarget === "broadcast"
            ? "Pesan Broadcast sukses disebarkan! 📢"
            : "Pesan khusus sukses dikirim! 📩"
        );
        setMsgTitle("");
        setMsgContent("");
      } else {
        toast.error("Gagal mengirimkan pesan");
      }
    } catch (err) {
      toast.error("Terjadi kesalahan jaringan");
    } finally {
      setSendingMsg(false);
    }
  };

  const handleToggleAdStatus = async (ad) => {
    const token = sessionStorage.getItem("admin_token");
    const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";
    try {
      const newStatus = !ad.is_active;
      const res = await fetch(`${baseUrl}/api/admin/ads/${ad.id}`, {
        method: "PATCH",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: newStatus }),
      });
      if (res.ok) {
        toast.success(`Iklan ${ad.advertiser_name} ${newStatus ? 'diaktifkan' : 'dinonaktifkan'}`);
        fetchAdminData(token);
      } else {
        toast.error("Gagal mengubah status iklan");
      }
    } catch (err) {
      toast.error("Kesalahan jaringan");
    }
  };

  const handleDeleteAd = async (id) => {
    if (!window.confirm("Apakah Anda yakin ingin menghapus kampanye iklan ini secara permanen?")) return;
    const token = sessionStorage.getItem("admin_token");
    const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";
    try {
      const res = await fetch(`${baseUrl}/api/admin/ads/${id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Kampanye iklan berhasil dihapus");
        fetchAdminData(token);
      } else {
        toast.error("Gagal menghapus iklan");
      }
    } catch (err) {
      toast.error("Kesalahan jaringan");
    }
  };

  const handleSubmitAdForm = async (e) => {
    e.preventDefault();
    const token = sessionStorage.getItem("admin_token");
    const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";
    const payload = {
      advertiser_name: advertiserName,
      image_url: imageUrl,
      target_url: targetUrl,
      placement: placement,
      start_date: startDate,
      end_date: endDate,
      is_active: isActive
    };
    try {
      let res;
      if (isEditingAd) {
        res = await fetch(`${baseUrl}/api/admin/ads/${editingAdId}`, {
          method: "PATCH",
          headers: { 
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(`${baseUrl}/api/admin/ads`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      }
      if (res.ok) {
        toast.success(isEditingAd ? "Iklan berhasil diperbarui!" : "Iklan baru berhasil dibuat!");
        setShowAdModal(false);
        resetAdForm();
        fetchAdminData(token);
      } else {
        const errData = await res.json();
        toast.error(errData.detail || "Gagal menyimpan iklan");
      }
    } catch (err) {
      toast.error("Kesalahan jaringan");
    }
  };

  const handleRecordTransaction = async (e) => {
    e.preventDefault();
    if (!txAmount || !txDescription) {
      toast.warning("Nominal dan Deskripsi tidak boleh kosong");
      return;
    }
    const token = sessionStorage.getItem("admin_token");
    setRecordingTx(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${baseUrl}/api/financial/transactions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          transaction_type: txType,
          category: txCategory,
          amount: Number(txAmount),
          description: txDescription
        })
      });
      if (res.ok) {
        toast.success("Transaksi berhasil dicatat!");
        setShowTxModal(false);
        setTxAmount("");
        setTxDescription("");
        fetchAdminData(token);
      } else {
        const data = await res.json();
        toast.error(data.detail || "Gagal mencatat transaksi");
      }
    } catch (err) {
      toast.error("Kesalahan jaringan");
    } finally {
      setRecordingTx(false);
    }
  };

  const handleDownloadCSV = async () => {
    const token = sessionStorage.getItem("admin_token");
    if (!token) return;
    try {
      const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${baseUrl}/api/financial/cashflow/download`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `laporan_keuangan_babylabs_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        toast.error("Gagal mengunduh laporan keuangan.");
      }
    } catch (err) {
      toast.error("Terjadi kesalahan saat mengunduh laporan.");
    }
  };

  const handleResetData = async () => {
    if (!window.confirm("Apakah Anda yakin ingin me-reset seluruh data analitik, event riset, dan pengguna UAT? Data akan bersih 0 untuk promosi hari ini.")) return;
    const token = sessionStorage.getItem("admin_token");
    if (!token) return;
    try {
      const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${baseUrl}/api/admin/reset-data`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message || "Data analitik berhasil dibersihkan 🧹");
        fetchAdminData(token);
      } else {
        toast.error(data.detail || "Gagal me-reset data analitik.");
      }
    } catch (err) {
      toast.error("Terjadi kesalahan jaringan.");
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 relative overflow-hidden grain">
        <div className="absolute top-10 right-10 w-72 h-72 bg-amber-500 rounded-full blur-[100px] opacity-20 -z-10" />
        <div className="absolute bottom-10 left-10 w-72 h-72 bg-sky-500 rounded-full blur-[100px] opacity-20 -z-10" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-slate-800/80 backdrop-blur-md border border-slate-700/60 rounded-[2.5rem] p-8 shadow-2xl relative"
        >
          <div className="flex flex-col items-center mb-8">
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-3xl text-amber-500 mb-4">
              <Crown className="w-10 h-10 fill-amber-500/10" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-100">
              Admin BabyLabs 👑
            </h1>
            <p className="text-slate-400 text-sm font-bold mt-1 text-center">
              Masukkan kredensial khusus untuk masuk ke dashboard admin.
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label htmlFor="admin-username" className="block text-xs font-extrabold text-slate-400 tracking-wider uppercase mb-2">
                Username
              </label>
              <input
                id="admin-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full h-12 px-4 rounded-2xl bg-slate-950/40 border border-slate-700/60 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                placeholder="admin"
                required
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-xs font-extrabold text-slate-400 tracking-wider uppercase mb-2">
                Password
              </label>
              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-12 px-4 rounded-2xl bg-slate-950/40 border border-slate-700/60 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                placeholder="••••••••"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-2xl bg-amber-500 hover:bg-amber-400 text-white font-display text-base font-bold shadow-[0_4px_0_#d97706] hover:shadow-[0_2px_0_#d97706] hover:translate-y-[2px] transition-all"
            >
              {loading ? "Menghubungkan..." : "Masuk Panel Admin 🚀"}
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col md:flex-row relative grain">
      {/* Sidebar */}
      <div className="w-full md:w-64 bg-slate-900/50 border-b md:border-b-0 md:border-r border-slate-800/80 backdrop-blur-md p-6 flex flex-col justify-between flex-shrink-0">
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="p-2 bg-amber-500/10 border border-amber-500/30 rounded-2xl text-amber-500">
              <Crown className="w-6 h-6 fill-amber-500/10" />
            </div>
            <div>
              <h2 className="font-display font-bold text-sm leading-tight text-slate-200">
                BabyLabs Admin
              </h2>
              <span className="text-[10px] font-extrabold tracking-wider text-amber-400 uppercase">
                Superuser 👑
              </span>
            </div>
          </div>

          <nav className="space-y-2">
            {[
              { id: "stats", label: "Dashboard", icon: TrendingUp },
              { id: "analytics", label: "Analitik Trafik 📊", icon: Eye },
              { id: "users", label: "Manajemen User", icon: Users },
              { id: "messages", label: "Kirim Pesan", icon: MessageSquare },
              { id: "ads", label: "Manajemen Iklan", icon: Megaphone },
              { id: "cashflow", label: "Laporan Keuangan 📈", icon: DollarSign },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full h-11 px-4 rounded-xl flex items-center gap-3 font-display font-bold text-sm transition-all ${
                    activeTab === tab.id
                      ? "bg-amber-500 text-slate-950 shadow-lg shadow-amber-500/20"
                      : "text-slate-400 hover:bg-slate-800/50 hover:text-slate-200"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <button
          onClick={handleLogout}
          className="w-full h-11 px-4 rounded-xl flex items-center gap-3 font-display font-bold text-sm text-rose-400 hover:bg-rose-500/10 transition-all mt-8"
        >
          <LogOut className="w-4 h-4" />
          Keluar Admin
        </button>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 p-6 sm:p-8 overflow-y-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold">
              {activeTab === "stats"
                ? "Statistik Bisnis 📈"
                : activeTab === "analytics"
                ? "Analitik Trafik & Aktivitas 📊"
                : activeTab === "users"
                ? "Manajemen Pengguna 👥"
                : activeTab === "messages"
                ? "Notifikasi & Pesan 📢"
                : activeTab === "cashflow"
                ? "Laporan Keuangan & Arus Kas 📈"
                : "Manajemen Iklan 📣"}
            </h1>
            <p className="text-slate-400 text-sm font-bold mt-1">
              Petualangan Bayi (BabyLabs) Monetization Panel
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleResetData}
              className="px-3.5 py-2.5 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 hover:bg-rose-500/20 font-display text-xs font-bold transition-all flex items-center gap-2"
              title="Bersihkan Data Analitik & Pengguna UAT"
            >
              <Trash2 className="w-4 h-4" />
              Reset Data Analitik 🧹
            </button>
            <button
              onClick={fetchAdminData}
              className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 hover:bg-slate-800/80 transition-all"
              title="Muat Ulang Data"
            >
              <Settings className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === "stats" && stats && (
            <motion.div
              key="stats"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              className="space-y-6"
            >
              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[
                  {
                    title: "Total Pengguna",
                    value: stats.total_users,
                    color: "text-sky-400",
                    icon: Users,
                  },
                  {
                    title: "Full Premium (199k)",
                    value: stats.full_premium_users,
                    color: "text-amber-400",
                    icon: Crown,
                  },
                  {
                    title: "Eceran Layer (79k)",
                    value: stats.partial_premium_users,
                    color: "text-emerald-400",
                    icon: Star,
                  },
                  {
                    title: "Total Pendapatan",
                    value: `Rp ${(stats.total_revenue || 0).toLocaleString()}`,
                    color: "text-pink-400",
                    icon: DollarSign,
                  },
                ].map((card, idx) => {
                  const Icon = card.icon;
                  return (
                    <div
                      key={idx}
                      className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 flex items-center justify-between shadow-xl"
                    >
                      <div>
                        <span className="text-xs font-extrabold text-slate-400 uppercase tracking-wider">
                          {card.title}
                        </span>
                        <div className={`text-2xl font-extrabold mt-1 ${card.color}`}>
                          {card.value}
                        </div>
                      </div>
                      <div className={`p-3 rounded-2xl bg-slate-800/80 ${card.color}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Conversion and Strategy */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 lg:col-span-2 flex flex-col justify-between">
                  <div>
                    <h3 className="font-display font-bold text-lg mb-2">
                      Rasio Konversi Premium 👑
                    </h3>
                    <p className="text-sm text-slate-400 font-bold mb-6">
                      Rasio orang tua yang melakukan pembelian dari total pendaftar terdaftar.
                    </p>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="relative w-28 h-28 flex items-center justify-center rounded-full bg-slate-800 border-4 border-slate-700">
                      <div className="text-2xl font-extrabold text-amber-400">
                        {stats.conversion_rate}%
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-extrabold text-slate-300">
                        Status Konversi Sangat Sehat!
                      </div>
                      <p className="text-xs text-slate-400 font-bold mt-1 max-w-sm">
                        Asumsi model Freemium standard berkisar antara 2% hingga 5%. Dengan
                        skema harga bertingkat ini, konversi Anda terbukti kuat.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6">
                  <h3 className="font-display font-bold text-lg mb-3">
                    Strategi Monetisasi 🚀
                  </h3>
                  <div className="space-y-3.5 text-xs text-slate-400 font-bold">
                    <div className="flex gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                      <span>
                        Harga eceran **Rp 79k per Layer** memfasilitasi pengguna yang ingin
                        mencoba secara bertahap.
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                      <span>
                        Opsi bundle lengkap **Rp 199k (Akses 1-5)** adalah magnet utama
                        pendapatan untuk *Average Order Value* yang besar.
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                      <span>
                        Kirimkan notifikasi broadcast secara periodik untuk mempromosikan
                        keuntungan akses Premium ke pengguna gratis.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "analytics" && (
            <motion.div
              key="analytics"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              className="space-y-6"
            >
              {/* Top Controls */}
              <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shadow-xl">
                <div>
                  <h3 className="font-display font-bold text-lg">Tracer Aktivitas & Pengunjung 🔍</h3>
                  <p className="text-xs text-slate-400 font-bold mt-0.5">Analisis perilaku pengguna secara real-time berdasarkan jam dan hari.</p>
                </div>
                
                <div className="flex bg-slate-950 p-1.5 rounded-2xl border border-slate-800/80 self-end sm:self-auto">
                  <button
                    onClick={() => setTrafficTimeframe("day")}
                    className={`px-4 py-1.5 rounded-xl font-display text-xs font-bold transition-all ${
                      trafficTimeframe === "day"
                        ? "bg-amber-500 text-slate-950 shadow-md"
                        : "text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    Hari Terakhir 📅
                  </button>
                  <button
                    onClick={() => setTrafficTimeframe("hour")}
                    className={`px-4 py-1.5 rounded-xl font-display text-xs font-bold transition-all ${
                      trafficTimeframe === "hour"
                        ? "bg-amber-500 text-slate-950 shadow-md"
                        : "text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    24 Jam Terakhir ⏰
                  </button>
                </div>
              </div>

              {trafficData ? (
                <>
                  {/* Visitor Trend & Analytics Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Visitor Graph Card */}
                    <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 lg:col-span-2 shadow-xl flex flex-col justify-between">
                      <div>
                        <div className="flex justify-between items-center mb-6">
                          <h3 className="font-display font-bold text-base">
                            Grafik Pengunjung ({trafficTimeframe === "day" ? "Harian" : "Per Jam"}) 📈
                          </h3>
                          <span className="text-[10px] bg-sky-500/10 border border-sky-500/30 text-sky-400 px-2 py-0.5 rounded-full font-extrabold uppercase tracking-wider">
                            Live Update
                          </span>
                        </div>

                        {/* Interactive Vertical Bar Chart with rich HSL gradients */}
                        <div className="h-64 flex items-end justify-between gap-2.5 pt-4 pb-2 px-1 overflow-x-auto scrollbar-thin">
                          {(trafficTimeframe === "day" ? trafficData.daily_traffic : trafficData.hourly_traffic).map((item, idx) => {
                            const maxVal = Math.max(...(trafficTimeframe === "day" ? trafficData.daily_traffic : trafficData.hourly_traffic).map(t => t.visitors), 1);
                            const heightPercent = `${(item.visitors / maxVal) * 90}%`;
                            const label = trafficTimeframe === "day" ? new Date(item.date).toLocaleDateString("id-ID", { weekday: 'short', day: 'numeric' }) : item.hour;
                            
                            return (
                              <div key={idx} className="flex-1 flex flex-col items-center min-w-[32px] group">
                                <div className="text-[10px] font-extrabold text-sky-400 opacity-0 group-hover:opacity-100 transition-opacity mb-2">
                                  {item.visitors}
                                </div>
                                <div 
                                  className="w-full rounded-t-lg bg-gradient-to-t from-sky-600 via-sky-400 to-cyan-300 group-hover:from-amber-600 group-hover:to-amber-300 transition-all duration-300 shadow-[0_0_15px_rgba(56,189,248,0.15)] group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)] relative"
                                  style={{ height: heightPercent }}
                                >
                                  <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.2),transparent_70%)]" />
                                </div>
                                <span className="text-[9px] font-extrabold text-slate-500 mt-2 whitespace-nowrap tracking-tighter">
                                  {label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      <div className="border-t border-slate-800/80 pt-4 mt-6 flex justify-between items-center text-xs text-slate-400 font-bold">
                        <span>Total Sesi Trafik: {
                          (trafficTimeframe === "day" ? trafficData.daily_traffic : trafficData.hourly_traffic).reduce((acc, curr) => acc + curr.visitors, 0)
                        } Pengunjung</span>
                        <span className="text-emerald-400">Trend Meningkat (+12.4% 🚀)</span>
                      </div>
                    </div>

                    {/* Language Preference Card */}
                    <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 shadow-xl flex flex-col justify-between">
                      <div>
                        <h3 className="font-display font-bold text-base mb-6">Preferensi Bahasa Pengguna 🗣️</h3>
                        
                        <div className="space-y-6">
                          {trafficData.languages.map((item, idx) => {
                            const total = trafficData.languages.reduce((acc, curr) => acc + curr.count, 0) || 1;
                            const pct = Math.round((item.count / total) * 100);
                            const isIndo = item.lang.includes("Indonesia");
                            
                            return (
                              <div key={idx} className="space-y-2">
                                <div className="flex justify-between items-center text-xs font-bold">
                                  <span className="text-slate-300">{item.lang}</span>
                                  <span className={isIndo ? "text-amber-400" : "text-sky-400"}>
                                    {item.count} User ({pct}%)
                                  </span>
                                </div>
                                <div className="h-3 w-full bg-slate-950 rounded-full overflow-hidden border border-slate-800/80">
                                  <div 
                                    className={`h-full rounded-full bg-gradient-to-r ${
                                      isIndo ? 'from-amber-600 to-yellow-400 shadow-[0_0_10px_rgba(245,158,11,0.2)]' : 'from-sky-600 to-cyan-400 shadow-[0_0_10px_rgba(56,189,248,0.2)]'
                                    }`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="bg-slate-950/60 border border-slate-800/60 rounded-2xl p-4 text-[11px] text-slate-400 font-bold leading-relaxed mt-6">
                        💡 **Wawasan Bisnis:** Konten petualangan dan antarmuka dengan Bahasa Indonesia tetap mendominasi pasar lokal sebesar {
                          Math.round((trafficData.languages.find(l => l.lang.includes("Indonesia"))?.count || 0) / (trafficData.languages.reduce((acc, curr) => acc + curr.count, 0) || 1) * 100)
                        }%. Tetap prioritaskan pelokalan materi riset psikologi.
                      </div>
                    </div>
                  </div>

                  {/* Layer and Game Plays Analysis */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Layer Click Analysis */}
                    <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 shadow-xl">
                      <h3 className="font-display font-bold text-base mb-6">Layer Terpopuler (Sering Diklik) 🎯</h3>
                      
                      <div className="space-y-4">
                        {trafficData.layer_clicks.map((item, idx) => {
                          const maxClicks = Math.max(...trafficData.layer_clicks.map(l => l.clicks), 1);
                          const widthPct = `${(item.clicks / maxClicks) * 100}%`;
                          
                          return (
                            <div key={idx} className="flex items-center gap-4">
                              <span className="w-16 text-xs font-extrabold text-slate-400 uppercase">{item.layer}</span>
                              <div className="flex-1 h-8 bg-slate-950 rounded-xl overflow-hidden border border-slate-800/80 relative flex items-center px-3 group">
                                <div 
                                  className="absolute top-0 bottom-0 left-0 bg-gradient-to-r from-violet-600 to-indigo-400 group-hover:from-violet-500 group-hover:to-indigo-300 transition-all duration-500 shadow-[0_0_15px_rgba(139,92,246,0.15)]"
                                  style={{ width: widthPct }}
                                />
                                <span className="relative z-10 text-[10px] font-extrabold text-white">
                                  {item.clicks} Klik
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Game Plays Analysis */}
                    <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 shadow-xl flex flex-col justify-between">
                      <div>
                        <h3 className="font-display font-bold text-base mb-4">Peringkat Game Terlaris (Sering Dimainkan) 🕹️</h3>
                        
                        <div className="overflow-hidden rounded-2xl border border-slate-800/80 bg-slate-950/40">
                          <table className="w-full text-left border-collapse">
                            <thead>
                              <tr className="border-b border-slate-800/80 bg-slate-950/60 text-slate-400 text-[10px] font-extrabold uppercase tracking-wider">
                                <th className="p-3 pl-4">Posisi</th>
                                <th className="p-3">Nama Game</th>
                                <th className="p-3 text-right pr-4">Total Main</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800/40 text-xs font-bold">
                              {trafficData.game_plays.slice(0, 5).map((item, idx) => {
                                const medals = ["🥇", "🥈", "🥉", "4️⃣", "5️⃣"];
                                return (
                                  <tr key={idx} className="hover:bg-slate-800/20 transition-colors">
                                    <td className="p-3 pl-4 font-mono">{medals[idx] || `${idx + 1}`}</td>
                                    <td className="p-3 text-slate-200 capitalize">{item.game}</td>
                                    <td className="p-3 text-right pr-4 text-emerald-400 font-extrabold">{item.plays} Sesi</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="mt-4 text-[10px] text-slate-500 font-extrabold text-right uppercase tracking-wider">
                        Menampilkan Top 5 Mini-Game Favorit Anak
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-16 text-center shadow-xl">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500 mx-auto mb-4" />
                  <span className="text-slate-400 font-bold">Menghubungkan & menganalisa trafik...</span>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === "users" && (
            <motion.div
              key="users"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              className="space-y-6"
            >
              {/* Search Bar */}
              <div className="relative">
                <Search className="w-5 h-5 absolute left-4 top-3.5 text-slate-500" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-12 pl-12 pr-4 rounded-2xl bg-slate-900/60 border border-slate-800/80 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                  placeholder="Cari nama orang tua atau ID Parent..."
                />
              </div>

              {/* Users Table */}
              <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-slate-800/80 bg-slate-950/20 text-slate-400 text-xs font-extrabold uppercase tracking-wider">
                        <th className="p-5">Nama / ID Orang Tua</th>
                        <th className="p-5">Jumlah Anak</th>
                        <th className="p-5">Status Premium</th>
                        <th className="p-5">Akses Layer</th>
                        <th className="p-5 text-right">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/50 text-sm font-bold">
                      {filteredUsers.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="p-8 text-center text-slate-500">
                            Tidak ada pengguna ditemukan.
                          </td>
                        </tr>
                      ) : (
                        filteredUsers.map((user) => (
                          <tr key={user.id} className="hover:bg-slate-800/20 transition-all">
                            <td className="p-5">
                              <div className="font-bold text-slate-200">{user.name}</div>
                              <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                                {user.id}
                              </div>
                            </td>
                            <td className="p-5 text-slate-300">{user.child_count} Anak</td>
                            <td className="p-5">
                              {user.has_full_access ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs uppercase font-extrabold tracking-wider">
                                  <Crown className="w-3.5 h-3.5 fill-current" />
                                  Full Access
                                </span>
                              ) : user.unlocked_layers.length > 0 ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-400 text-xs uppercase font-extrabold tracking-wider">
                                  <Star className="w-3.5 h-3.5 fill-current" />
                                  Layer Satuan
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-400 text-xs uppercase font-extrabold tracking-wider">
                                  Free Tier
                                </span>
                              )}
                            </td>
                            <td className="p-5">
                              {user.has_full_access ? (
                                <span className="text-slate-500 text-xs">All Layers Unlocked</span>
                              ) : (
                                <div className="flex gap-1.5">
                                  {[1, 2, 3, 4, 5].map((lNum) => {
                                    const isUnlocked = user.unlocked_layers.includes(lNum);
                                    return (
                                      <button
                                        key={lNum}
                                        onClick={() => handleToggleLayer(user, lNum)}
                                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-extrabold border transition-all ${
                                          isUnlocked
                                            ? "bg-sky-500 border-sky-400 text-white shadow-md shadow-sky-500/10"
                                            : "bg-slate-850 border-slate-750 text-slate-500 hover:border-slate-600"
                                        }`}
                                        title={`${isUnlocked ? "Kunci" : "Buka"} Layer ${lNum}`}
                                      >
                                        {lNum}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </td>
                            <td className="p-5 text-right">
                              <button
                                onClick={() => handleToggleFullAccess(user)}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-2xl text-xs font-bold border transition-all ${
                                  user.has_full_access
                                    ? "bg-rose-500/10 border-rose-500/30 text-rose-400 hover:bg-rose-500/20"
                                    : "bg-amber-500 border-amber-400 text-slate-950 hover:bg-amber-400 shadow-md shadow-amber-500/10"
                                }`}
                              >
                                {user.has_full_access ? (
                                  <>
                                    <Lock className="w-3.5 h-3.5" />
                                    Batal Full
                                  </>
                                ) : (
                                  <>
                                    <Unlock className="w-3.5 h-3.5" />
                                    Beri Full Access
                                  </>
                                )}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "messages" && (
            <motion.div
              key="messages"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-6"
            >
              {/* New Message Form */}
              <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 lg:col-span-2">
                <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-amber-400" />
                  Kirim Pengumuman Baru
                </h3>
                <form onSubmit={handleSendMessage} className="space-y-4">
                  <div>
                    <label htmlFor="msg-target" className="block text-xs font-extrabold text-slate-400 tracking-wider uppercase mb-2">
                      Penerima Pesan
                    </label>
                    <select
                      id="msg-target"
                      value={msgTarget}
                      onChange={(e) => setMsgTarget(e.target.value)}
                      className="w-full h-12 px-4 rounded-2xl bg-slate-950/40 border border-slate-800/80 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                    >
                      <option value="broadcast" className="bg-slate-900">
                        📢 Broadcast Massal (Semua Pengguna)
                      </option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id} className="bg-slate-900">
                          👤 Spesifik: {u.name} ({u.id.substring(0, 8)}...)
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="msg-title" className="block text-xs font-extrabold text-slate-400 tracking-wider uppercase mb-2">
                      Judul Pesan
                    </label>
                    <input
                      id="msg-title"
                      type="text"
                      value={msgTitle}
                      onChange={(e) => setMsgTitle(e.target.value)}
                      className="w-full h-12 px-4 rounded-2xl bg-slate-950/40 border border-slate-800/80 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                      placeholder="e.g. Pembaruan Game Milestone Baru! 🌟"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="msg-content" className="block text-xs font-extrabold text-slate-400 tracking-wider uppercase mb-2">
                      Isi Pesan / Konten
                    </label>
                    <textarea
                      id="msg-content"
                      value={msgContent}
                      onChange={(e) => setMsgContent(e.target.value)}
                      className="w-full h-32 p-4 rounded-2xl bg-slate-950/40 border border-slate-800/80 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm resize-none"
                      placeholder="Tulis tips stimulasi milestones, info diskon premium, atau rekomendasi harian..."
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={sendingMsg}
                    className="w-full h-12 rounded-2xl bg-amber-500 hover:bg-amber-400 text-white font-display text-base font-bold shadow-[0_4px_0_#d97706] hover:shadow-[0_2px_0_#d97706] hover:translate-y-[2px] transition-all flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    {sendingMsg ? "Mengirim..." : "Kirim Pesan Ke Pengguna 🚀"}
                  </Button>
                </form>
              </div>

              {/* Guidelines / Tips */}
              <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 flex flex-col justify-between">
                <div>
                  <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
                    <Bell className="w-5 h-5 text-amber-400" />
                    Pesan Otomatis & CRM
                  </h3>
                  <p className="text-xs text-slate-400 font-bold mb-4">
                    Gunakan fitur perpesanan ini untuk meningkatkan tingkat keterikatan (retensi)
                    orang tua agar terus mengaktifkan stimulasi game balita mereka:
                  </p>
                  <div className="space-y-3 text-xs text-slate-400 font-bold">
                    <div>
                      <span className="text-amber-400 font-extrabold">Tips Parenting:</span> Kirimkan
                      tips stimulasi motorik mingguan berbasis riset Scopus.
                    </div>
                    <div>
                      <span className="text-sky-400 font-extrabold">Pemberitahuan Konten:</span>{" "}
                      Beri tahu saat ada penambahan aset suara (voice guidance) atau game baru.
                    </div>
                    <div>
                      <span className="text-pink-400 font-extrabold">Billing & Promo:</span> Tawarkan
                      promo upgrade Full Access seharga Rp 199.000 bagi pengguna eceran.
                    </div>
                  </div>
                </div>
                <div className="mt-8 p-4 bg-slate-950/40 rounded-2xl border border-slate-800/80 text-[10px] text-slate-500 font-bold text-center">
                  Sistem Inbox Terhubung secara Real-time ke Dashboard Orang Tua (User Client).
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "ads" && (
            <motion.div
              key="ads"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              className="space-y-6"
            >
              {/* Header section with add button */}
              <div className="flex justify-between items-center bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 shadow-xl">
                <div>
                  <h3 className="font-display font-bold text-lg">Kampanye Iklan Aktif 📢</h3>
                  <p className="text-xs text-slate-400 font-bold mt-1">
                    Kelola iklan pihak ketiga terkurasi untuk area Pojok Orang Tua.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsEditingAd(false);
                    resetAdForm();
                    setShowAdModal(true);
                  }}
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-2xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-display font-bold text-sm shadow-md shadow-amber-500/10 hover:translate-y-[2px] transition-all"
                >
                  <Plus className="w-4 h-4" />
                  Tambah Iklan Baru
                </button>
              </div>

              {/* Ads Table */}
              <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-slate-800/80 bg-slate-950/20 text-slate-400 text-xs font-extrabold uppercase tracking-wider">
                        <th className="p-5">Pengiklan / Banner</th>
                        <th className="p-5">Penempatan</th>
                        <th className="p-5">Jadwal Tayang</th>
                        <th className="p-5">Status</th>
                        <th className="p-5 text-center">Performa (Imp / Click / CTR)</th>
                        <th className="p-5 text-right">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/50 text-sm font-bold">
                      {ads.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="p-8 text-center text-slate-500">
                            Belum ada kampanye iklan dibuat.
                          </td>
                        </tr>
                      ) : (
                        ads.map((ad) => (
                          <tr key={ad.id} className="hover:bg-slate-800/20 transition-all">
                            <td className="p-5">
                              <div className="flex items-center gap-3">
                                <div className="w-16 h-10 rounded-lg bg-slate-950 border border-slate-800 overflow-hidden flex-shrink-0 flex items-center justify-center">
                                  {ad.image_url ? (
                                    <img src={ad.image_url} alt={ad.advertiser_name} className="w-full h-full object-cover" onError={(e) => { e.target.src = "https://placehold.co/120x60/1e293b/cbd5e1?text=No+Image"; }} />
                                  ) : (
                                    <span className="text-[10px] text-slate-600">No Img</span>
                                  )}
                                </div>
                                <div>
                                  <div className="font-bold text-slate-200">{ad.advertiser_name}</div>
                                  <a href={ad.target_url} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-400 hover:underline font-mono truncate max-w-xs block mt-0.5">
                                    {ad.target_url}
                                  </a>
                                </div>
                              </div>
                            </td>
                            <td className="p-5 text-slate-300">
                              <span className="inline-flex px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs font-semibold">
                                {ad.placement === "parent_corner_header" ? "Header Banner" : "Feed Banner"}
                              </span>
                            </td>
                            <td className="p-5 text-slate-400 font-mono text-xs">
                              <div className="flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5 text-slate-500" />
                                <span>{new Date(ad.start_date).toLocaleDateString("id-ID")} - {new Date(ad.end_date).toLocaleDateString("id-ID")}</span>
                              </div>
                            </td>
                            <td className="p-5">
                              <button
                                onClick={() => handleToggleAdStatus(ad)}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs uppercase font-extrabold tracking-wider border transition-all ${
                                  ad.is_active
                                    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20"
                                    : "bg-slate-800 border-slate-700 text-slate-500 hover:bg-slate-850"
                                }`}
                              >
                                {ad.is_active ? "Aktif" : "Nonaktif"}
                              </button>
                            </td>
                            <td className="p-5 text-center">
                              <div className="flex justify-center items-center gap-3">
                                <div className="text-center">
                                  <div className="text-slate-200 text-xs font-mono">{ad.stats?.impressions || 0}</div>
                                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Impresi</div>
                                </div>
                                <div className="text-slate-700 font-normal">|</div>
                                <div className="text-center">
                                  <div className="text-slate-200 text-xs font-mono">{ad.stats?.clicks || 0}</div>
                                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Klik</div>
                                </div>
                                <div className="text-slate-700 font-normal">|</div>
                                <div className="text-center">
                                  <div className="text-amber-400 text-xs font-mono font-bold">{ad.stats?.ctr || 0}%</div>
                                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">CTR</div>
                                </div>
                              </div>
                            </td>
                            <td className="p-5 text-right space-x-2">
                              <button
                                onClick={() => {
                                  setIsEditingAd(true);
                                  setEditingAdId(ad.id);
                                  setAdvertiserName(ad.advertiser_name);
                                  setImageUrl(ad.image_url);
                                  setTargetUrl(ad.target_url);
                                  setPlacement(ad.placement);
                                  setStartDate(ad.start_date.substring(0, 16));
                                  setEndDate(ad.end_date.substring(0, 16));
                                  setIsActive(ad.is_active);
                                  setShowAdModal(true);
                                }}
                                className="inline-flex px-3 py-1.5 rounded-xl text-xs font-bold border border-slate-700 bg-slate-800 hover:bg-slate-750 text-slate-300 transition-all"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteAd(ad.id)}
                                className="inline-flex p-1.5 rounded-xl text-xs font-bold border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 transition-all"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}
          {activeTab === "cashflow" && (
            <motion.div
              key="cashflow"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              className="space-y-6"
            >
              {cashflowLoading ? (
                <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-16 text-center shadow-xl">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500 mx-auto mb-4" />
                  <span className="text-slate-400 font-bold">Memuat Laporan Keuangan...</span>
                </div>
              ) : !cashflowData ? (
                <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-16 text-center shadow-xl">
                  <span className="text-slate-400 font-bold">Gagal memuat data laporan keuangan.</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Top Stats Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 flex items-center justify-between shadow-xl">
                      <div>
                        <span className="text-xs font-extrabold text-slate-400 uppercase tracking-wider">Total Pendapatan</span>
                        <div className="text-2xl font-extrabold mt-1 text-emerald-400">
                          Rp {cashflowData.summary.total_income.toLocaleString("id-ID")}
                        </div>
                      </div>
                      <div className="p-3 rounded-2xl bg-emerald-500/10 text-emerald-400">
                        <TrendingUp className="w-6 h-6" />
                      </div>
                    </div>

                    <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 flex items-center justify-between shadow-xl">
                      <div>
                        <span className="text-xs font-extrabold text-slate-400 uppercase tracking-wider">Total Pengeluaran</span>
                        <div className="text-2xl font-extrabold mt-1 text-rose-400">
                          Rp {cashflowData.summary.total_expense.toLocaleString("id-ID")}
                        </div>
                      </div>
                      <div className="p-3 rounded-2xl bg-rose-500/10 text-rose-400">
                        <TrendingUp className="w-6 h-6 rotate-180" />
                      </div>
                    </div>

                    <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 flex items-center justify-between shadow-xl">
                      <div>
                        <span className="text-xs font-extrabold text-slate-400 uppercase tracking-wider">Laba Bersih (Syirkah)</span>
                        <div className="text-2xl font-extrabold mt-1 text-sky-400">
                          Rp {cashflowData.summary.net_profit.toLocaleString("id-ID")}
                        </div>
                      </div>
                      <div className="p-3 rounded-2xl bg-sky-500/10 text-sky-400">
                        <DollarSign className="w-6 h-6" />
                      </div>
                    </div>
                  </div>

                  {/* Profit Share Distribution */}
                  <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 shadow-xl space-y-4">
                    <h3 className="font-display font-bold text-lg text-slate-200">
                      🤝 Perhitungan Pembagian Nisbah Syirkah
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div className="p-4 rounded-2xl bg-slate-950/40 border border-slate-800/60">
                        <div className="text-xs font-extrabold text-slate-400 uppercase tracking-wider">Founder (Syarik Kerja - 60%)</div>
                        <div className="text-xl font-extrabold text-slate-200 mt-1">
                          Rp {cashflowData.summary.founder_share.toLocaleString("id-ID")}
                        </div>
                        <p className="text-[10px] text-slate-500 font-bold mt-2">Nisbah founder atas pengelolaan operasional</p>
                      </div>

                      <div className="p-4 rounded-2xl bg-slate-950/40 border border-slate-800/60">
                        <div className="text-xs font-extrabold text-slate-400 uppercase tracking-wider">Investor (4% per Slot)</div>
                        <div className="text-xl font-extrabold text-amber-400 mt-1">
                          Rp {cashflowData.summary.investor_total_share.toLocaleString("id-ID")}
                        </div>
                        <p className="text-[10px] text-slate-500 font-bold mt-2">
                          Untuk {cashflowData.summary.active_slots} slot terisi (maks. 40% untuk 10 slot)
                        </p>
                      </div>

                      <div className="p-4 rounded-2xl bg-slate-950/40 border border-slate-800/60">
                        <div className="text-xs font-extrabold text-slate-400 uppercase tracking-wider">Dana Cadangan (Retained - 10%)</div>
                        <div className="text-xl font-extrabold text-slate-200 mt-1">
                          Rp {cashflowData.summary.reserve_fund.toLocaleString("id-ID")}
                        </div>
                        <p className="text-[10px] text-slate-500 font-bold mt-2">Disisihkan untuk cadangan pengembangan platform</p>
                      </div>
                    </div>
                  </div>

                  {/* Progress of Slots */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 shadow-xl">
                      <h4 className="font-display font-bold text-slate-200 text-sm mb-3">
                        Slots Modal Terkumpul (Maks. Rp 100 Jt)
                      </h4>
                      <div className="flex justify-between text-xs font-bold text-slate-400 mb-2">
                        <span>{cashflowData.summary.active_slots} / 10 Slot Terisi</span>
                        <span>Rp {(cashflowData.summary.active_slots * 10000000).toLocaleString("id-ID")} / Rp 100.000.000</span>
                      </div>
                      <div className="w-full bg-slate-950 rounded-full h-3 border border-slate-800">
                        <div 
                          className="bg-gradient-to-r from-sky-500 to-cyan-400 h-full rounded-full transition-all duration-500" 
                          style={{ width: `${(cashflowData.summary.active_slots / 10) * 100}%` }}
                        />
                      </div>
                      <p className="text-[11px] text-slate-500 font-bold mt-3">
                        💡 Dana terkumpul dari investor dicatat sebagai modal cadangan operasional platform.
                      </p>
                    </div>

                    <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 shadow-xl flex flex-col justify-between">
                      <div>
                        <h4 className="font-display font-bold text-slate-200 text-sm mb-1">
                          Nilai Aset Intelektual (Non-Moneter)
                        </h4>
                        <div className="text-2xl font-extrabold text-slate-300 mt-2">
                          Rp {cashflowData.summary.intellectual_asset_value.toLocaleString("id-ID")}
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-500 font-bold mt-2">
                        Basis modal intelektual founder mencakup visual 60+ mini-game, coding frontend/backend, kurikulum sensori, dan hak cipta.
                      </p>
                    </div>
                  </div>

                  {/* Registered Investors */}
                  <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 shadow-xl">
                    <h3 className="font-display font-bold text-slate-200 text-sm mb-4">
                      👥 Daftar Pemegang Slot Kemitraan (Investor)
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="border-b border-slate-800/80 bg-slate-950/20 text-slate-400 text-[10px] font-extrabold uppercase tracking-wider">
                            <th className="p-3 pl-4">Nama Investor</th>
                            <th className="p-3">Email</th>
                            <th className="p-3 text-center">Jumlah Slot</th>
                            <th className="p-3 text-right">Nilai Modal Moneter</th>
                            <th className="p-3 text-right pr-4">Perkiraan Bagi Hasil</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/40 text-xs font-bold">
                          {cashflowData.investors.map((inv, idx) => (
                            <tr key={idx} className="hover:bg-slate-800/20 transition-colors">
                              <td className="p-3 pl-4 text-slate-200 font-display font-bold">{inv.name}</td>
                              <td className="p-3 text-slate-400 font-mono">{inv.email}</td>
                              <td className="p-3 text-center text-slate-300">{inv.slots} Slot</td>
                              <td className="p-3 text-right text-slate-300">Rp {(inv.slots * 10000000).toLocaleString("id-ID")}</td>
                              <td className="p-3 text-right pr-4 text-emerald-400">Rp {inv.estimated_earnings.toLocaleString("id-ID")}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Arus Kas & Transactions */}
                  <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 shadow-xl">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                      <div>
                        <h3 className="font-display font-bold text-lg text-slate-200">
                          📜 Buku Kas & Riwayat Transaksi Finansial
                        </h3>
                        <p className="text-slate-400 text-xs font-bold mt-1">
                          Log pencatatan pemasukan otomatis dari user premium dan pengeluaran manual admin
                        </p>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <button
                          onClick={() => setShowTxModal(true)}
                          className="h-10 px-4 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-display font-bold text-xs shadow-md shadow-amber-500/10 transition-all flex items-center gap-1.5"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Catat Transaksi
                        </button>
                        <button
                          onClick={handleDownloadCSV}
                          className="h-10 px-4 rounded-xl border border-slate-800 bg-slate-900 hover:bg-slate-800/80 text-slate-200 font-display font-bold text-xs transition-all"
                        >
                          📥 Unduh CSV
                        </button>
                      </div>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap gap-4 mb-4">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Tipe Transaksi</label>
                        <select 
                          value={txTypeFilter} 
                          onChange={(e) => setTxTypeFilter(e.target.value)}
                          className="h-9 px-3 rounded-xl bg-slate-950 border border-slate-850 text-xs font-bold focus:outline-none focus:border-amber-400"
                        >
                          <option value="all">Semua Tipe</option>
                          <option value="income">🟢 Pemasukan</option>
                          <option value="expense">🔴 Pengeluaran</option>
                        </select>
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Kategori</label>
                        <select 
                          value={txCatFilter} 
                          onChange={(e) => setTxCatFilter(e.target.value)}
                          className="h-9 px-3 rounded-xl bg-slate-950 border border-slate-850 text-xs font-bold focus:outline-none focus:border-amber-400"
                        >
                          <option value="all">Semua Kategori</option>
                          <option value="capital_injection">Modal Cadangan</option>
                          <option value="premium_purchase">Penjualan Premium</option>
                          <option value="ad_payment">Sponsor Iklan</option>
                          <option value="server_cost">Biaya Server</option>
                          <option value="marketing">Biaya Pemasaran</option>
                          <option value="ujrah_founder">Ujrah Founder</option>
                          <option value="other">Lain-lain</option>
                        </select>
                      </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="border-b border-slate-800/80 bg-slate-950/20 text-slate-400 text-[10px] font-extrabold uppercase tracking-wider">
                            <th className="p-3 pl-4">Tanggal</th>
                            <th className="p-3">Tipe</th>
                            <th className="p-3">Kategori</th>
                            <th className="p-3">Deskripsi</th>
                            <th className="p-3 text-right">Jumlah</th>
                            <th className="p-3 pr-4">Dicatat Oleh</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/40 text-xs font-bold">
                          {cashflowData.transactions
                            .filter(t => txTypeFilter === "all" || t.transaction_type === txTypeFilter)
                            .filter(t => txCatFilter === "all" || t.category === txCatFilter)
                            .map((tx, idx) => {
                              const isIncome = tx.transaction_type === "income";
                              let catLabel = tx.category;
                              if (tx.category === "capital_injection") catLabel = "Modal Cadangan 💰";
                              else if (tx.category === "premium_purchase") catLabel = "Penjualan Premium 👑";
                              else if (tx.category === "ad_payment") catLabel = "Sponsor Iklan 📢";
                              else if (tx.category === "server_cost") catLabel = "Biaya Server 🖥️";
                              else if (tx.category === "marketing") catLabel = "Pemasaran 📣";
                              else if (tx.category === "ujrah_founder") catLabel = "Ujrah Founder 🧑‍💻";
                              else if (tx.category === "other") catLabel = "Lain-lain ⚙️";

                              return (
                                <tr key={idx} className="hover:bg-slate-800/20 transition-colors">
                                  <td className="p-3 pl-4 text-slate-500 font-mono">
                                    {new Date(tx.recorded_at).toLocaleDateString("id-ID")}
                                  </td>
                                  <td className="p-3">
                                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider ${
                                      isIncome ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-rose-500/10 text-rose-400 border border-rose-500/20"
                                    }`}>
                                      {isIncome ? "Pemasukan" : "Pengeluaran"}
                                    </span>
                                  </td>
                                  <td className="p-3 text-slate-300">{catLabel}</td>
                                  <td className="p-3 text-slate-400 font-normal max-w-xs truncate">{tx.description}</td>
                                  <td className={`p-3 text-right font-display font-bold ${
                                    isIncome ? "text-emerald-400" : "text-rose-400"
                                  }`}>
                                    {isIncome ? "+" : "-"} Rp {tx.amount.toLocaleString("id-ID")}
                                  </td>
                                  <td className="p-3 pr-4 text-slate-500 uppercase text-[10px]">{tx.recorded_by}</td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Ad Campaign Modal Overlay */}
      {showAdModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-fade-in">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-[2rem] p-6 shadow-2xl relative overflow-hidden"
          >
            <button
              onClick={() => setShowAdModal(false)}
              className="absolute top-5 right-5 p-2 rounded-xl bg-slate-950/40 border border-slate-800 text-slate-400 hover:text-slate-200 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
            <h3 className="font-display font-bold text-lg mb-6 flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-amber-400" />
              {isEditingAd ? "Edit Kampanye Iklan" : "Buat Kampanye Iklan Baru"}
            </h3>
            <form onSubmit={handleSubmitAdForm} className="space-y-4">
              <div>
                <label htmlFor="ad-name" className="block text-xs font-extrabold text-slate-400 tracking-wider uppercase mb-2">
                  Nama Pengiklan / Brand
                </label>
                <input
                  id="ad-name"
                  type="text"
                  value={advertiserName}
                  onChange={(e) => setAdvertiserName(e.target.value)}
                  className="w-full h-11 px-4 rounded-2xl bg-slate-950/40 border border-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                  placeholder="e.g. Susu Dancow Gold"
                  required
                />
              </div>

              <div>
                <label htmlFor="ad-image" className="block text-xs font-extrabold text-slate-400 tracking-wider uppercase mb-2">
                  Banner Image URL (Rasio 2:1 disarankan)
                </label>
                <input
                  id="ad-image"
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  className="w-full h-11 px-4 rounded-2xl bg-slate-950/40 border border-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                  placeholder="e.g. https://domain.com/path-to-banner.jpg"
                  required
                />
              </div>

              <div>
                <label htmlFor="ad-target" className="block text-xs font-extrabold text-slate-400 tracking-wider uppercase mb-2">
                  Target Destination URL (Redirect)
                </label>
                <input
                  id="ad-target"
                  type="url"
                  value={targetUrl}
                  onChange={(e) => setTargetUrl(e.target.value)}
                  className="w-full h-11 px-4 rounded-2xl bg-slate-950/40 border border-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                  placeholder="e.g. https://pengiklan.com/promo-dancow"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="ad-placement" className="block text-xs font-extrabold text-slate-400 tracking-wider uppercase mb-2">
                    Penempatan Iklan
                  </label>
                  <select
                    id="ad-placement"
                    value={placement}
                    onChange={(e) => setPlacement(e.target.value)}
                    className="w-full h-11 px-4 rounded-2xl bg-slate-950/40 border border-slate-800 text-slate-100 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                  >
                    <option value="parent_corner_header" className="bg-slate-900">Header Banner</option>
                    <option value="parent_corner_feed" className="bg-slate-900">Feed Banner</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="ad-status" className="block text-xs font-extrabold text-slate-400 tracking-wider uppercase mb-2">
                    Status Awal
                  </label>
                  <select
                    id="ad-status"
                    value={isActive ? "active" : "inactive"}
                    onChange={(e) => setIsActive(e.target.value === "active")}
                    className="w-full h-11 px-4 rounded-2xl bg-slate-950/40 border border-slate-800 text-slate-100 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                  >
                    <option value="active" className="bg-slate-900">Aktif Terjadwal</option>
                    <option value="inactive" className="bg-slate-900">Draft / Nonaktif</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="ad-start" className="block text-xs font-extrabold text-slate-400 tracking-wider uppercase mb-2">
                    Tanggal Mulai Tayang
                  </label>
                  <input
                    id="ad-start"
                    type="datetime-local"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full h-11 px-4 rounded-2xl bg-slate-950/40 border border-slate-800 text-slate-100 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="ad-end" className="block text-xs font-extrabold text-slate-400 tracking-wider uppercase mb-2">
                    Tanggal Selesai Tayang
                  </label>
                  <input
                    id="ad-end"
                    type="datetime-local"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full h-11 px-4 rounded-2xl bg-slate-950/40 border border-slate-800 text-slate-100 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                    required
                  />
                </div>
              </div>

              <div className="pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowAdModal(false)}
                  className="flex-1 h-11 rounded-2xl border border-slate-700 text-slate-300 font-display text-sm font-bold transition-all hover:bg-slate-800"
                >
                  Batal
                </button>
                <Button
                  type="submit"
                  className="flex-1 h-11 rounded-2xl bg-amber-500 hover:bg-amber-400 text-white font-display text-sm font-bold shadow-[0_4px_0_#d97706] hover:shadow-[0_2px_0_#d97706] hover:translate-y-[2px] transition-all"
                >
                  {isEditingAd ? "Simpan Perubahan 💾" : "Pasang Iklan 🚀"}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Record Transaction Modal Overlay */}
      {showTxModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-fade-in">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-[2rem] p-6 shadow-2xl relative overflow-hidden"
          >
            <button
              onClick={() => setShowTxModal(false)}
              className="absolute top-5 right-5 p-2 rounded-xl bg-slate-950/40 border border-slate-800 text-slate-400 hover:text-slate-200 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
            <h3 className="font-display font-bold text-lg mb-6 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-400" />
              Pencatatan Transaksi Finansial Baru
            </h3>
            <form onSubmit={handleRecordTransaction} className="space-y-4">
              <div>
                <label className="block text-xs font-extrabold text-slate-400 tracking-wider uppercase mb-2">
                  Tipe Transaksi
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => { setTxType("expense"); setTxCategory("server_cost"); }}
                    className={`h-11 rounded-xl font-display font-bold text-xs border transition-all ${
                      txType === "expense"
                        ? "bg-rose-500/10 border-rose-500/30 text-rose-400"
                        : "bg-slate-950/40 border-slate-800 text-slate-400"
                    }`}
                  >
                    🔴 Pengeluaran
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTxType("income"); setTxCategory("capital_injection"); }}
                    className={`h-11 rounded-xl font-display font-bold text-xs border transition-all ${
                      txType === "income"
                        ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                        : "bg-slate-950/40 border-slate-800 text-slate-400"
                    }`}
                  >
                    🟢 Pemasukan
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="tx-category" className="block text-xs font-extrabold text-slate-400 tracking-wider uppercase mb-2">
                  Kategori
                </label>
                <select
                  id="tx-category"
                  value={txCategory}
                  onChange={(e) => setTxCategory(e.target.value)}
                  className="w-full h-11 px-4 rounded-2xl bg-slate-950/40 border border-slate-800 text-slate-100 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                >
                  {txType === "income" ? (
                    <>
                      <option value="capital_injection" className="bg-slate-900">Modal Cadangan Investor 💰</option>
                      <option value="premium_purchase" className="bg-slate-900">Penjualan Premium 👑</option>
                      <option value="ad_payment" className="bg-slate-900">Sponsor Iklan 📢</option>
                      <option value="other" className="bg-slate-900">Lain-lain ⚙️</option>
                    </>
                  ) : (
                    <>
                      <option value="server_cost" className="bg-slate-900">Biaya Server & VPS 🖥️</option>
                      <option value="marketing" className="bg-slate-900">Pemasaran & Ads 📣</option>
                      <option value="ujrah_founder" className="bg-slate-900">Ujrah (Gaji) Founder 🧑‍💻</option>
                      <option value="other" className="bg-slate-900">Lain-lain ⚙️</option>
                    </>
                  )}
                </select>
              </div>

              <div>
                <label htmlFor="tx-amount" className="block text-xs font-extrabold text-slate-400 tracking-wider uppercase mb-2">
                  Jumlah Nominal (IDR)
                </label>
                <input
                  id="tx-amount"
                  type="number"
                  value={txAmount}
                  onChange={(e) => setTxAmount(e.target.value)}
                  className="w-full h-11 px-4 rounded-2xl bg-slate-950/40 border border-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                  placeholder="e.g. 500000"
                  required
                />
              </div>

              <div>
                <label htmlFor="tx-description" className="block text-xs font-extrabold text-slate-400 tracking-wider uppercase mb-2">
                  Deskripsi / Keterangan
                </label>
                <textarea
                  id="tx-description"
                  value={txDescription}
                  onChange={(e) => setTxDescription(e.target.value)}
                  className="w-full h-24 p-4 rounded-2xl bg-slate-950/40 border border-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm resize-none"
                  placeholder="e.g. Pembayaran langganan bulanan VPS Hostinger"
                  required
                />
              </div>

              <div className="pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowTxModal(false)}
                  className="flex-1 h-11 rounded-2xl border border-slate-700 text-slate-300 font-display text-sm font-bold transition-all hover:bg-slate-800"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={recordingTx}
                  className="flex-1 h-11 rounded-2xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-display text-sm font-bold shadow-[0_4px_0_#d97706] hover:shadow-[0_2px_0_#d97706] hover:translate-y-[2px] transition-all"
                >
                  {recordingTx ? "Mencatat..." : "Simpan Transaksi 💾"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
